/* Setup — compact layout (option 1a, one scroll). Status cards (AI assistant,
   Login, Plan), then Where it works (lists, ignore lists, work schedule),
   Default behaviour (AI regime, hot transition, follow-ups) and List overrides.
   Every change saves on its own, like Content and Monetisation: typing is
   debounced, choices save straight away. There is no Save button. */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../api/client';
import type { Account, AccountSubscriptionStatus, OfList } from '../../../../api/client';
import { useAutosave } from '../../../../hooks/useAutosave';
import { calendarDaysUntil, isOnDemand, isPlanCovered } from '../../../../utils/subscription';
import { ReloginModal } from '../../../Onboarding/ReloginModal';
import { buildRegimeSelectOptions, regimeCompletionStep } from '../../aiRegimeOptions';
import { Card, Dropdown, Icon, Row, SaveStatus, Seg, TextArea, Toggle } from '../kit';
import { font as f, mergeStatus } from '../style';
import { C } from '../tokens';
import { useTracker } from '../salesLadder/useTracker';
import { HOT_MODES, PROTECTED_LISTS, draftOf, groupWindows, hhmm, overrideOf, overridePayload, payloadOf, weeklyHours, windowChanges, type Draft, type Override } from './model';
import { Overrides } from './Overrides';
import { AddLists, BigToggle, CardBtn, Chip, ChipRow, ConfirmRemove, StatusCard, Stepper, SubRow } from './parts';
import { ScheduleModal } from './ScheduleModal';
import { Spinner } from '../../../Spinner';

const PLAN_KEYS: Record<string, string> = { monthly_core_4_features: 'setup.planNames.assistant', monthly_core_no_assistant: 'setup.planNames.noAssistant' };

export function SetupPage({ account, onAccountUpdate }: { account: Account; onAccountUpdate: (a: Account) => void }) {
  const { t } = useTranslation('assistantCompact');
  const { t: tc } = useTranslation('chatAssistant');
  const { t: td } = useTranslation('dashboard');
  const navigate = useNavigate();
  const id = account.account_id;
  const acct = account as Account & { subscription_plan_name?: string | null; subscription_plan_code?: string | null; subscription_active?: boolean | null; subscription_period_end?: string | null };

  /* ── Account settings: one draft, only changed columns are sent ── */
  const [draft, setDraft] = useState<Draft>(() => draftOf(account));
  const saved = useRef(payloadOf(draft));
  const auto = useAutosave<Draft>({
    value: draft,
    save: async (d) => {
      const next = payloadOf(d);
      const diff = Object.fromEntries(Object.entries(next).filter(([k, v]) => JSON.stringify(saved.current[k]) !== JSON.stringify(v)));
      if (!Object.keys(diff).length) return;
      // The server stores scope + lists as one pair (default_on_all_fans + lists_to_speak):
      // always send both, or adding a list alone is dropped.
      if ('ai_working_scope' in diff || 'ai_selected_folders' in diff) {
        diff.ai_working_scope = next.ai_working_scope;
        diff.ai_selected_folders = next.ai_selected_folders;
      }
      const updated = await api.updateAccount(id, diff as Partial<Account>);
      saved.current = next;
      onAccountUpdate(updated);
    },
  });
  const set = (p: Partial<Draft>) => setDraft((d) => ({ ...d, ...p }));
  const setNow = (p: Partial<Draft>) => { set(p); window.setTimeout(auto.flush, 0); };

  /* ── AI master switch: its own endpoint, saved straight away ──── */
  const [aiOn, setAiOn] = useState(account.is_enabled);
  const tracker = useTracker();
  const toggleAi = (v: boolean) => {
    setAiOn(v);
    tracker.track(() => api.toggleAccount(id, v)).then((u) => { setAiOn(u.is_enabled ?? v); onAccountUpdate(u); }, () => {});
  };

  /* ── Lists (the account's OnlyFans lists) ──────────────────────── */
  const [lists, setLists] = useState<OfList[]>([]);
  const [listsLoading, setListsLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const loadLists = useCallback(async () => {
    try { setLists(await api.getAccountOfLists(id)); } catch { /* keep what we have */ } finally { setListsLoading(false); }
  }, [id]);
  useEffect(() => { void loadLists(); }, [loadLists]);
  const refreshLists = async () => {
    setSyncing(true);
    try { await api.refreshListsAndWait(id); await loadLists(); } finally { setSyncing(false); }
  };
  const listName = (uuid: string) => lists.find((l) => l.list_uuid === uuid)?.of_list_name || uuid;
  const listNames = lists.map((l) => l.of_list_name).filter((n): n is string => !!n);

  /* ── List overrides: saved row by row against the server copy ─── */
  const [ov, setOv] = useState<Override[]>([]);
  const [ovReady, setOvReady] = useState(false);
  const [ovFailed, setOvFailed] = useState(false);
  const server = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    let off = false;
    api.getFolderAiSetups(id).then(
      (rows) => {
        if (off) return;
        const o = rows.map(overrideOf);
        server.current = new Map(o.map((r) => [r.list, JSON.stringify(overridePayload(r))]));
        setOv(o);
        setOvReady(true);
      },
      () => { if (!off) setOvFailed(true); },
    );
    return () => { off = true; };
  }, [id]);
  const ovAuto = useAutosave<Override[]>({
    value: ov,
    enabled: ovReady,
    save: async (rows) => {
      const live = rows.filter((r) => r.list);
      const keep = new Set(live.map((r) => r.list));
      for (const uuid of Array.from(server.current.keys())) {
        if (!keep.has(uuid)) { await api.deleteFolderAiSetup(id, uuid); server.current.delete(uuid); }
      }
      for (const r of live) {
        const body = overridePayload(r);
        const json = JSON.stringify(body);
        if (server.current.get(r.list) === json) continue;
        await api.upsertFolderAiSetup(id, body);
        server.current.set(r.list, json);
      }
    },
  });
  const setOverrides = (rows: Override[], now?: boolean) => { setOv(rows); if (now) window.setTimeout(ovAuto.flush, 0); };

  const status = mergeStatus(auto, ovAuto, tracker);

  /* ── Status cards: login health + plan ─────────────────────────── */
  const [relogin, setRelogin] = useState(false);
  const [throttled, setThrottled] = useState(false);
  const loggedIn = !!account.is_logged_in;
  useEffect(() => {
    let off = false;
    if (!loggedIn) { setThrottled(false); return; }
    api.getAccountSessions(id).then((s) => { if (!off) setThrottled(!!s.throttled); }, () => { if (!off) setThrottled(false); });
    return () => { off = true; };
  }, [id, loggedIn]);

  const [sub, setSub] = useState<AccountSubscriptionStatus | null>(null);
  useEffect(() => {
    let off = false;
    api.getAccountsSubscriptionStatus().then((m) => { if (!off) setSub(m[id] ?? null); }, () => {});
    return () => { off = true; };
  }, [id]);
  const planLoading = sub === null && !acct.subscription_plan_name && !acct.subscription_plan_code;
  // On demand accounts have no Subscription; they are covered unless billing paused them.
  const onDemand = isOnDemand(sub);
  const planActive = sub ? isPlanCovered(sub) : !!acct.subscription_active;
  const expiry = sub?.current_period_end ? new Date(sub.current_period_end) : acct.subscription_period_end ? new Date(acct.subscription_period_end) : null;
  const daysLeft = expiry ? calendarDaysUntil(expiry) : sub ? sub.days_left : null;
  const planCode = sub?.plan_code || acct.subscription_plan_code;
  const planName = onDemand ? td('accountView.onDemand') : (planCode && PLAN_KEYS[planCode] ? tc(PLAN_KEYS[planCode]) : null) || acct.subscription_plan_name || planCode || (planLoading ? tc('setup.checkingPlan') : tc('setup.noActivePlan'));
  const planSub = planLoading ? <Spinner size={11} label={t('loading')} /> : onDemand ? td(planActive ? 'accountView.onDemandSub' : 'accountView.onDemandPausedSub') : !planActive ? tc('setup.planInactive') : daysLeft === null ? tc('setup.planActive') : daysLeft === 0 ? tc('setup.expiresToday') : tc('setup.daysLeft', { count: daysLeft });
  const expiryTxt = !onDemand && planActive && expiry ? tc('setup.expiresOn', { date: expiry.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) }) : '';

  /* ── Where it works ────────────────────────────────────────────── */
  const [confirm, setConfirm] = useState<string | null>(null);
  const [schedOpen, setSchedOpen] = useState(false);
  const specific = draft.scope === 'specific_folders';
  // A list is either worked or ignored: each picker leaves out the other's picks.
  const workedNames = specific ? draft.folders.map(listName) : [];
  const removeIgnore = (n: string) => set({ ignore: draft.ignore.filter((x) => x !== n) });
  const regimeOpts = buildRegimeSelectOptions(draft.regime).map((r) => ({ id: r.value, label: r.label, sub: r.description }));
  const regime = regimeOpts.find((r) => r.id === draft.regime);
  const endStep = regimeCompletionStep(draft.regime);
  const scheduled = draft.sched === 'scheduled';
  const noWin = scheduled && !draft.windows.length;
  const dayRange = (days: boolean[]) => {
    const on = days.map((v, i) => (v ? i : -1)).filter((i) => i >= 0);
    if (on.length === 7) return t('su.everyDay');
    if (!on.length) return t('su.noDays');
    const run = on.length > 1 && on.every((v, k) => !k || v === (on[k - 1] ?? -2) + 1);
    return run ? `${t(`su.dn.${on[0]}`)}–${t(`su.dn.${on[on.length - 1]}`)}` : on.map((d) => t(`su.dn.${d}`)).join(', ');
  };
  const winChips = scheduled ? groupWindows(draft.windows).map((w) => {
    const ch = windowChanges(w);
    const reg = w.regime ? buildRegimeSelectOptions(w.regime).find((r) => r.value === w.regime)?.label : null;
    const chat = w.call_chatter != null && regimeCompletionStep(w.regime ?? draft.regime) !== null ? `${t('su.callChatter')}: ${w.call_chatter ? t('su.on') : t('su.off')}` : null;
    return [dayRange(w.days), `${hhmm(w.start)}–${hhmm(w.end)}`, reg, chat, ch ? t('su.nChanges', { count: ch }) : null].filter(Boolean).join(' · ');
  }) : [];

  /* ── Follow-ups ────────────────────────────────────────────────── */
  const step = (i: number, p: { min?: number; max?: number }) => {
    const steps = draft.steps.map((s, j) => {
      if (j !== i) return s;
      const min = Math.min(60, Math.max(1, p.min ?? s.min));
      const max = Math.min(60, Math.max(min, p.max ?? s.max));
      return { min, max };
    });
    set({ steps });
  };
  const hotShort = (h: string) => t(`su.hotShort.${h}`);
  const hotLong = (h: string) => (h === 'custom' ? '' : tc(`setup.hotOptions.${h}Desc`));

  return (
    <div className="cpt-root cpt-layout" style={{ display: 'flex', background: C.surface, border: `1px solid ${C.borderLo}`, borderRadius: 10 }}>
      <main className="cpt-main" style={{ flex: 1, minWidth: 0, padding: '16px 24px 40px', display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* Status */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 10 }}>
          <StatusCard icon="power" label={tc('setup.aiAssistant')} value={aiOn ? tc('setup.active') : tc('setup.paused')} sub={account.kill_switch ? t('su.killSwitchOverride') : aiOn ? tc('setup.replyingAuto') : t('su.notReplying')} active={aiOn && !account.kill_switch}>
            <BigToggle on={aiOn} onChange={toggleAi} title={tc('setup.aiAssistantInfo')} />
          </StatusCard>
          <StatusCard
            icon="log-in"
            label={t('su.login')}
            value={throttled ? t('su.rateLimited') : loggedIn ? t('su.connected') : t('su.notConnected')}
            sub={throttled ? t('su.throttledHint') : loggedIn ? t('su.sessionActive') : t('su.logInToStart')}
            subWarn={throttled || !loggedIn}
            active={loggedIn && !throttled}
          >
            <CardBtn cta={throttled || !loggedIn} onClick={() => (throttled ? navigate(`/account/${id}/settings`) : loggedIn ? setRelogin(true) : navigate(`/accounts/new?resume=${id}`))}>
              {throttled ? t('su.sessionSettings') : loggedIn ? t('su.relogin') : t('su.logIn')}
            </CardBtn>
          </StatusCard>
          <StatusCard icon="credit-card" label={tc('setup.plan')} value={planName} sub={planSub} tip={expiryTxt || undefined} subWarn={!planLoading && (!planActive || (daysLeft !== null && daysLeft <= 3))} active={planActive}>
            <CardBtn cta={!planLoading && !planActive} onClick={() => navigate('/payments')}>{tc('setup.topUp')}</CardBtn>
          </StatusCard>
        </div>
        {relogin && <ReloginModal accountId={id} platform={account.platform} onClose={() => setRelogin(false)} onConnected={() => setRelogin(false)} />}

        {/* Where it works */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Head title={t('su.where')} hint={t('su.whereHint')}>
            <SaveStatus status={status.status} error={status.error} onRetry={status.retry} />
            <button type="button" onClick={() => void refreshLists()} disabled={syncing} title={t('su.refreshLists')} className={`cpt-hover-row${syncing ? ' om-busy' : ''}`} style={{ width: 26, height: 26, border: 0, borderRadius: 6, background: 'transparent', color: C.sub, cursor: syncing ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="refresh-cw" size={13} />
            </button>
          </Head>
          <Card clip={false}>
            <div>
              <Row label={t('su.lists')} help={specific ? t('su.scopeSpecific') : t('su.scopeAll')}>
                <Seg options={[{ id: 'all_folders', label: t('su.allLists') }, { id: 'specific_folders', label: t('su.specificLists') }]} value={draft.scope} onChange={(v) => setNow({ scope: v })} />
              </Row>
              {specific && (
                <ChipRow>
                  {draft.folders.map((u) => <Chip key={u} label={listName(u)} onRemove={() => setNow({ folders: draft.folders.filter((x) => x !== u) })} />)}
                  {!draft.folders.length && <span style={{ display: 'flex', alignItems: 'center', gap: 5, font: f(400, 12), color: C.purplePale }}><Icon name="alert-circle" size={12} />{t('su.noListsChosen')}</span>}
                  <AddLists disabled={listsLoading} pool={lists.filter((l) => !draft.folders.includes(l.list_uuid) && !draft.ignore.includes(l.of_list_name)).map((l) => ({ id: l.list_uuid, label: l.of_list_name || l.list_uuid }))} onAdd={(a) => setNow({ folders: [...draft.folders, ...a] })} />
                </ChipRow>
              )}
            </div>
            <div>
              <Row label={<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{tc('setup.ignoreLists')}<span style={{ font: f(600, 10.5), color: C.purplePale, background: C.tint16, padding: '1px 6px', borderRadius: 99 }}>{draft.ignore.length}</span></span>} help={t('su.ignoreHelp')} />
              <ChipRow>
                {draft.ignore.map((n) => <Chip key={n} label={n} onRemove={() => (PROTECTED_LISTS.includes(n) ? setConfirm(n) : setNow({ ignore: draft.ignore.filter((x) => x !== n) }))} />)}
                {!draft.ignore.length && <span style={{ font: f(400, 12), color: C.muted }}>{t('su.noIgnored')}</span>}
                <AddLists disabled={listsLoading} pool={listNames.filter((n) => !draft.ignore.includes(n) && !workedNames.includes(n)).map((n) => ({ id: n, label: n }))} onAdd={(a) => setNow({ ignore: [...draft.ignore, ...a] })} />
              </ChipRow>
            </div>
            <div>
              <Row
                label={tc('setup.schedule.title')}
                help={<span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>{noWin && <Icon name="alert-circle" size={12} />}{!scheduled ? t('su.alwaysHelp') : noWin ? t('su.noWinHelp') : t('su.schedHelp', { count: Math.round(weeklyHours(draft.windows)) })}</span>}
                helpColor={noWin ? C.purplePale : undefined}
              >
                {scheduled && (
                  <button type="button" onClick={() => setSchedOpen(true)} className="cpt-hover-border" style={{ height: 26, padding: '0 10px', display: 'flex', alignItems: 'center', gap: 5, background: 'transparent', border: `1px solid ${C.borderHi}`, borderRadius: 6, color: 'var(--om-body)', font: f(500, 12), cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    <Icon name="calendar" size={12} />{tc('setup.schedule.editButton')}
                  </button>
                )}
                <Seg options={[{ id: 'always_on', label: tc('setup.schedule.alwaysOn') }, { id: 'scheduled', label: tc('setup.schedule.scheduled') }]} value={draft.sched} onChange={(v) => setNow({ sched: v })} />
              </Row>
              {winChips.length > 0 && (
                <ChipRow>
                  {winChips.map((c, i) => (
                    <button key={i} type="button" title={c} onClick={() => setSchedOpen(true)} style={{ height: 24, maxWidth: '100%', padding: '0 9px', display: 'flex', alignItems: 'center', gap: 5, background: C.tint16, border: 0, borderRadius: 6, font: f(500, 12), color: C.purplePale, whiteSpace: 'nowrap', cursor: 'pointer' }}>
                      <Icon name="clock" size={12} style={{ flex: 'none' }} /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{c}</span>
                    </button>
                  ))}
                </ChipRow>
              )}
            </div>
          </Card>
        </div>

        {/* Default behaviour */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Head title={t('su.defaults')} hint={t('su.defaultsHint')} />
          <Card clip={false}>
            <div>
              <Row label={t('su.aiRegime')} help={regime?.sub}>
                <Dropdown value={draft.regime} options={regimeOpts} onChange={(v) => setNow({ regime: v })} width={200} menuWidth={320} />
              </Row>
              {endStep !== null && (
                <SubRow
                  label={t('su.callChatter')}
                  help={<span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>{!draft.chatter && <Icon name="alert-circle" size={12} />}{draft.chatter ? t('su.chatterOn', { step: endStep }) : t('su.chatterOff', { step: endStep })}</span>}
                  helpColor={draft.chatter ? undefined : C.purplePale}
                >
                  <Toggle on={draft.chatter} onChange={(v) => setNow({ chatter: v })} />
                </SubRow>
              )}
            </div>
            <div>
              <Row label={t('su.hot')} help={<span title={hotLong(draft.hot)}>{hotShort(draft.hot)}</span>}>
                <Seg options={HOT_MODES.map((h) => ({ id: h, label: tc(`setup.hotOptions.${h}`), title: hotLong(h) || undefined }))} value={draft.hot} onChange={(v) => setNow({ hot: v })} />
              </Row>
              {draft.hot === 'custom' && (
                <div style={{ padding: '0 14px 10px' }}>
                  <TextArea value={draft.hotPrompt} onChange={(v) => set({ hotPrompt: v })} placeholder={tc('setup.hotTransitionPlaceholder')} height={72} />
                </div>
              )}
            </div>
            <Row label={tc('setup.followUpMessages')} help={<span title={tc('setup.followUpMessagesInfo')}>{draft.fu ? t('su.fuOn') : t('su.fuOff')}</span>}>
              <Toggle on={draft.fu} onChange={(v) => setNow({ fu: v })} />
            </Row>
            {draft.fu && (
              <SubRow label={tc('setup.followUpCount')}>
                <Seg options={[1, 2, 3].map((n) => ({ id: n, label: <span style={{ display: 'inline-block', width: 20, textAlign: 'center' }}>{n}</span> }))} value={draft.fuN} onChange={(v) => setNow({ fuN: v })} />
              </SubRow>
            )}
            {draft.fu && draft.steps.slice(0, draft.fuN).map((s, i) => (
              <SubRow key={i} label={draft.fuN > 1 ? t('su.fuStep', { n: i + 1 }) : t('su.sendAfter')} help={i === 0 ? t('su.afterReply') : t('su.afterPrev', { n: i })}>
                <Stepper value={s.min} unit={tc('setup.minSuffix')} onDec={() => step(i, { min: s.min - 1 })} onInc={() => step(i, { min: s.min + 1 })} />
                <span style={{ color: C.muted }}>–</span>
                <Stepper value={s.max} unit={tc('setup.minSuffix')} onDec={() => step(i, { max: s.max - 1 })} onInc={() => step(i, { max: s.max + 1 })} />
              </SubRow>
            ))}
            {draft.fu && (
              <SubRow label={t('su.autoDelete')} help={draft.autoDel ? t('su.autoDelOn') : t('su.autoDelOff')}>
                {draft.autoDel && <Stepper value={draft.delMin} unit={tc('setup.minSuffix')} onDec={() => set({ delMin: Math.max(5, draft.delMin - 5) })} onInc={() => set({ delMin: Math.min(1440, draft.delMin + 5) })} />}
                <Toggle on={draft.autoDel} onChange={(v) => setNow({ autoDel: v })} />
              </SubRow>
            )}
          </Card>
        </div>

        {/* List overrides */}
        {ovFailed
          ? <div style={{ display: 'flex', alignItems: 'center', gap: 6, font: f(400, 12), color: C.purplePale }}><Icon name="alert-circle" size={13} />{t('su.ovLoadFailed')}</div>
          : <Overrides rows={ov} lists={lists} loading={!ovReady} onChange={setOverrides} />}
      </main>

      {schedOpen && (
        <ScheduleModal
          tz={draft.tz}
          windows={draft.windows}
          regime={regime?.label ?? draft.regime}
          regimeId={draft.regime}
          chatter={draft.chatter}
          lists={listNames}
          onTz={(tz) => setNow({ tz })}
          onWindows={(windows) => set({ windows })}
          onClose={() => { setSchedOpen(false); window.setTimeout(auto.flush, 0); }}
        />
      )}
      {confirm && (
        <ConfirmRemove
          title={t('su.cfTitle', { name: confirm })}
          body={confirm === 'Notifications' ? t('su.cfNotifications') : t('su.cfSpam')}
          onKeep={() => setConfirm(null)}
          onRemove={() => { removeIgnore(confirm); setConfirm(null); window.setTimeout(auto.flush, 0); }}
        />
      )}
    </div>
  );
}

function Head({ title, hint, children }: { title: string; hint: string; children?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minHeight: 26 }}>
      <span style={{ font: f(600, 13), color: C.text }}>{title}</span>
      <span style={{ font: f(400, 11.5), color: C.muted }}>{hint}</span>
      {children && <><span style={{ flex: 1 }} />{children}</>}
    </div>
  );
}
